import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * Configuration for individual page read-only control
 */
export interface PageConfig {
  readOnly: boolean; // Whether the page should be in read-only mode
}

/**
 * Feature flags configuration structure
 * Use EITHER readOnlyMode (global) OR pages (granular), but not both
 */
export interface FeatureFlags {
  readOnlyMode?: boolean; // Global read-only mode - when set, applies to all pages
  pages?: {
    physicalTable?: PageConfig;
    resourceManagement?: PageConfig;
    userManagement?: PageConfig;
    roleManagement?: PageConfig;
  };
}

/**
 * Available page keys for feature flag checks
 */
export type PageKey = keyof FeatureFlags['pages'];

/**
 * Service to manage feature flags for controlling read-only modes
 * 
 * This service provides methods to:
 * - Check if the entire application is in read-only mode
 * - Determine if specific pages should be read-only
 * - Control access to write operations across the UI
 * 
 * All pages are always accessible, but may have restricted write access
 */
@Injectable({
  providedIn: 'root'
})
export class FeatureFlagService {
  private readonly featureFlags: FeatureFlags;

  constructor() {
    this.featureFlags = environment.featureFlags;
  }

  /**
   * Check if the entire application is in read-only mode
   * When true, all write operations should be disabled across the UI
   */
  isGlobalReadOnlyMode(): boolean {
    return this.featureFlags.readOnlyMode ?? false;
  }

  /**
   * Check if a specific page should be in read-only mode
   * Uses either global readOnlyMode OR individual page settings
   * @param page - The page to check (physicalTable, resourceManagement, userManagement, roleManagement)
   */
  isPageReadOnly(page: PageKey): boolean {
    // If global read-only mode is set, use that (takes precedence)
    if (this.featureFlags.readOnlyMode !== undefined) {
      return this.featureFlags.readOnlyMode;
    }

    // Otherwise, check individual page setting (defaults to false if not configured)
    const pageConfig = this.featureFlags.pages?.[page];
    return pageConfig?.readOnly ?? false;
  }
}